import {
  Checkbox,
  Ellipsis,
  GenericTable,
  Modal,
  NewButton,
  NewButtonVariant,
  TooltipTrigger,
} from "@visiblethread/react-components";
import EmptyDocumentIcon from "components/EmptyDocumentIcon";
import EmptyState from "components/EmptyState";
import { getDocumentIcon } from "components/Modal/ChooseFilesModal/utils";
import { checkedTypes, modalTypes } from "constants/types";
import { FILE_SOURCES } from "layout/documentUploader/constants";
import { FileX2Icon } from "lucide-react";
import * as PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { getFileFormat } from "utils/getFileFormat";
import {
  isDocumentWordless,
  WORDLESS_DOCUMENT_TOOLTIP,
} from "utils/reportSelectability";

const DISABLED_TOOLTIP = "This document type is not supported for this action";

const SelectUploadedDocumentsModal = ({
  documents = [],
  onSelect,
  disabledFormats = [],
  disabledTooltip = DISABLED_TOOLTIP,
  disableWordless = false,
}) => {
  const dispatch = useDispatch();
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  const hasFormatDisabling = disabledFormats.length > 0;
  const hasDisabling = hasFormatDisabling || disableWordless;

  const isFormatDisabled = useCallback(
    (doc) => {
      if (!hasFormatDisabling || doc.source === "VT_EDITOR") {
        return false;
      }
      const format = getFileFormat(doc?.name);
      return disabledFormats.includes(format);
    },
    [disabledFormats, hasFormatDisabling]
  );

  const isWordlessDisabled = useCallback(
    (doc) => disableWordless && isDocumentWordless(doc),
    [disableWordless]
  );

  const isRowDisabled = useCallback(
    (doc) => isWordlessDisabled(doc) || isFormatDisabled(doc),
    [isWordlessDisabled, isFormatDisabled]
  );

  const getRowTooltip = useCallback(
    (doc) =>
      isWordlessDisabled(doc) ? WORDLESS_DOCUMENT_TOOLTIP : disabledTooltip,
    [isWordlessDisabled, disabledTooltip]
  );

  const handleClose = useCallback(() => {
    dispatch(hideModal(modalTypes.SELECT_UPLOADED_DOCUMENTS));
  }, [dispatch]);

  const handleConfirm = useCallback(() => {
    if (onSelect) {
      onSelect(selectedDocuments);
    }
    handleClose();
  }, [onSelect, selectedDocuments, handleClose]);

  const handleRowSelection = useCallback(
    (document) => {
      if (isRowDisabled(document)) return;

      setSelectedDocuments((prev) => {
        const isSelected = prev.some((doc) => doc.id === document.id);
        if (isSelected) {
          return prev.filter((doc) => doc.id !== document.id);
        }
        return [...prev, document];
      });
    },
    [isRowDisabled]
  );

  const columns = useMemo(
    () => [
      {
        header: "",
        id: "checkbox",
        size: 40,
        minSize: 40,
        enableResizing: false,
        cell: ({ row }) => {
          const document = row.original;
          const isSelected = selectedDocuments.some(
            (doc) => doc.id === document.id
          );
          return (
            <Checkbox
              state={isSelected ? checkedTypes.CHECKED : checkedTypes.EMPTY}
              handleChange={() => handleRowSelection(document)}
              disabled={isRowDisabled(document)}
            />
          );
        },
      },
      {
        header: "Name",
        accessorKey: "name",
        id: "name",
        size: 235,
        minSize: 150,
        cell: ({ row }) => {
          const document = row.original;
          const disabled = isRowDisabled(document);
          const wordless = isWordlessDisabled(document);
          return (
            <TooltipTrigger
              isEnabled={disabled}
              content={getRowTooltip(document)}
            >
              <div
                className="upload_files_modal__table__file_name"
                data-clarity-mask="True"
              >
                {getDocumentIcon(document.name)}
                <Ellipsis text={document.name} enableTooltip={!disabled} />
                {wordless && <EmptyDocumentIcon />}
              </div>
            </TooltipTrigger>
          );
        },
      },
      {
        header: "Source",
        accessorKey: "source",
        id: "source",
        size: 90,
        minSize: 90,
        enableResizing: false,
        cell: ({ row }) => {
          return (
            <span className="upload_files_modal__table__file_source">
              <Ellipsis text={FILE_SOURCES[row.original.source] || "-"} />
            </span>
          );
        },
      },
    ],

    [
      selectedDocuments,
      handleRowSelection,
      isRowDisabled,
      isWordlessDisabled,
      getRowTooltip,
    ]
  );

  return (
    <Modal
      className="upload_files_modal"
      onDismiss={handleClose}
      restrictWidth={false}
      backdrop
      show
    >
      <Modal.Header>
        <span>Select Documents</span>
      </Modal.Header>
      <Modal.Body>
        {documents.length === 0 ? (
          <EmptyState
            icon={<FileX2Icon size={24} color="#533ffb" />}
            title="No Documents"
            description="Please navigate to Documents view to upload documents."
          />
        ) : (
          <>
            <div className="upload_files_modal__description">
              Select the document you wish to generate the outline from.
            </div>
            <div className="upload_files_modal__table_wrapper">
              <GenericTable
                data={documents}
                columns={columns}
                onRowClick={handleRowSelection}
                defaultSortColumnIndex={1}
                rowClassName="choose_files_modal__body__details__row"
                isRowDisabled={hasDisabling ? isRowDisabled : undefined}
              />
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <NewButton variant={NewButtonVariant.l2} onClick={handleClose}>
          Cancel
        </NewButton>

        <NewButton
          variant={NewButtonVariant.l1}
          disabled={selectedDocuments.length === 0}
          onClick={handleConfirm}
        >
          Next
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

SelectUploadedDocumentsModal.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      pageCount: PropTypes.number,
      source: PropTypes.string,
      wordCount: PropTypes.number,
    })
  ),
  onSelect: PropTypes.func.isRequired,
  disabledFormats: PropTypes.arrayOf(PropTypes.string),
  disabledTooltip: PropTypes.string,
  disableWordless: PropTypes.bool,
};

export default SelectUploadedDocumentsModal;
